/**
 * Banner block templates.
 *
 * `classic` is what every page saved before templates existed renders: the picture with the
 * text box floating in the middle of it, sized by the `layout` aspect setting. It stays the
 * default and keeps its exact rendering — switching away has to be the user's choice.
 *
 * The six after it come from the designer's mockup (`Banner block templates mobile`). All of
 * them are the same thing a banner has always been — media filling the frame with words on
 * top — arranged differently: where the text sits, how dark the scrim under it is, and which
 * of the buttons the block already has get drawn.
 *
 * The template lives in BannerBlock.View, which already round-trips. The one thing none of
 * the existing fields could carry is the small line above the title (`Studio Podil`,
 * `Just printed`) — that is `badge`, stored in the block's JSON payload.
 */
export const BANNER_TEMPLATES = {
  // Legacy layout — do not restyle, existing pages render with this.
  classic: 'classic',

  // Templates.
  coverTop: 'coverTop',
  coverTitle: 'coverTitle',
  coverLeft: 'coverLeft',
  coverCenter: 'coverCenter',
  coverVideo: 'coverVideo',
  coverBadge: 'coverBadge',
};

/**
 * How each template arranges the frame. Read by BannerCover, and by nothing else — a
 * template is this row plus the thumbnail beside it in the picker.
 *
 * - `align`     — where the block of text sits in the frame
 * - `scrim`     — the gradient over the media, dark enough to read the words at arm's length
 * - `height`    — the frame's height on a phone; `null` follows the block's own height setting
 * - `badge`     — draws the small line above the title (`pill`, `eyebrow`, or none)
 * - `buttons`   — `stack` one per row, `row` side by side, `none` to leave the CTA off
 * - `titleCase` — the uppercase, letter-spaced treatment of the title-only template
 */
export const BANNER_TEMPLATE_LAYOUTS = {
  [BANNER_TEMPLATES.coverTop]: {
    align: 'center',
    scrim: 'linear-gradient(to bottom, rgba(20,22,26,.55), rgba(20,22,26,.35) 45%, rgba(20,22,26,.7))',
    height: 560,
    badge: 'eyebrow',
    buttons: 'none',
    titleCase: false,
  },
  [BANNER_TEMPLATES.coverTitle]: {
    align: 'center',
    scrim: 'linear-gradient(to bottom, rgba(20,22,26,.3), rgba(20,22,26,.62))',
    height: 560,
    badge: 'none',
    buttons: 'none',
    titleCase: true,
  },
  [BANNER_TEMPLATES.coverLeft]: {
    align: 'bottom',
    scrim: 'linear-gradient(120deg, rgba(20,22,26,.82), rgba(20,22,26,.5) 55%, rgba(20,22,26,.25))',
    height: 560,
    badge: 'none',
    buttons: 'row',
    titleCase: false,
  },
  [BANNER_TEMPLATES.coverCenter]: {
    align: 'center',
    scrim: 'rgba(20,22,26,.55)',
    height: 560,
    badge: 'none',
    buttons: 'stack',
    titleCase: false,
  },
  [BANNER_TEMPLATES.coverVideo]: {
    align: 'bottom',
    scrim: 'linear-gradient(to top, rgba(20,22,26,.85), rgba(20,22,26,.15) 55%, rgba(20,22,26,.35))',
    height: 560,
    badge: 'live',
    buttons: 'stack',
    titleCase: false,
  },
  [BANNER_TEMPLATES.coverBadge]: {
    align: 'bottom',
    scrim: 'linear-gradient(to top, rgba(20,22,26,.9), rgba(20,22,26,0))',
    height: 420,
    badge: 'pill',
    buttons: 'stack',
    titleCase: false,
  },
};

/** True for everything except the original layout, which renders through the legacy markup. */
export const isBannerCoverTemplate = (template) => Boolean(BANNER_TEMPLATE_LAYOUTS[template]);
